import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { inbox } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Switch } from '@repletoryreach/sdk-ui/inputs';
import { notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';

/**
 * Whether new conversations nobody has yet are given to me (8 October 2026). Conversations without an assignee are
 * shared out in turn among the people marked available, within the conversation's team when it has one; nothing in
 * the console could mark anyone available, so that sharing never happened. Off means away: conversations already
 * yours stay yours.
 */
export function AvailabilitySwitch() {
  const t = useT();
  const qc = useQueryClient();
  const { user } = useAuth();
  const agents = useQuery({ queryKey: ['agents'], queryFn: () => inbox.listAgents(), staleTime: 30_000 });
  const mine = (agents.data?.data ?? []).find((a) => a.user_id === user?.id);
  const on = mine?.status_code === 'available';
  const save = useMutation({
    mutationFn: (next: boolean) => inbox.updateMyAvailability(next ? 'available' : 'away'),
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['agents'] }); },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('inbox.available.label'))
  });
  return (
    <div className="rr-inbox2__available" data-guide="inbox-available" title={t('inbox.available.hint')}>
      <Switch
        size="xs"
        label={t('inbox.available.label')}
        checked={save.isPending ? !!save.variables : on}
        disabled={agents.isPending || save.isPending}
        onChange={(checked) => save.mutate(checked)}
      />
    </div>
  );
}
